<template>
  <div class="text-left">
    <GoBackButton />
    <div class="bg-card">
      <h2>{{ $t('pages.killer-questions-templates.manage-folder.title', 'Manage Folders') }}</h2>
      <b-alert
        variant="danger"
        :show="isEditFolderSelected"
      >
        <font-awesome-icon
          icon="exclamation-triangle"
          class="danger-icon m-auto"
        />
        {{ $t('pages.killer-questions-templates.manage-folder.title', 'Must select a folder!') }}
      </b-alert>
      <div class="mb-4">
        <div>{{ $t('pages.killer-questions-templates.manage-folder.must-select-folder', 'Select the folder you want to modify') }}</div>
        <div>
          <FoldersTree
            :folders-data="killerqFolders"
            :show-questions="false"
            :is-expanded="true"
            :is-folder-select="true"
            :selected-folder="selectedEditFolder"
            @node-click="selectEditFolder"
          />
        </div>
      </div>
      <div class="mb-4">
        <div>{{ $t('pages.killer-questions-templates.manage-folder.edit-folder-name', 'Modify the name of your folder') }}</div>
        <div>
          <label
            for="folder-name"
            class="mb-0"
          >{{ $t('pages.killer-questions-templates.manage-folder.folder-name', 'Folder name:') }}</label>
          <b-form-input
            id="folder-name"
            v-model="$v.folderName.$model"
            :state="$v.folderName.$dirty ? !$v.folderName.$error : null"
            size="sm"
          />
        </div>
      </div>
      <div class="mb-4">
        <div>{{ $t('pages.killer-questions-templates.manage-folder.folder-location', 'Modify the location of your folder') }}</div>
        <div>
          <FoldersTree
            :folders-data="killerqFolders"
            :show-questions="false"
            :is-expanded="true"
            :is-folder-select="true"
            :selected-folder="selectedSaveFolder"
            @node-click="selectSaveFolder"
          />
        </div>
      </div>
      <div class="row justify-content-center m-2">
        <b-button
          variant="success"
          size="sm"
          class="mr-1"
          @click="updateFolder"
        >
          {{ $t('generic.save') }}
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="mr-1"
          @click="deleteFolder()"
        >
          {{ $t('generic.delete') }}
        </b-button>
        <b-button
          variant="secondary"
          size="sm"
          @click="$router.go(-1)"
        >
          {{ $t('generic.cancel') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import GoBackButton from '@/components/jobs/shared/GoBackButton';
import FoldersTree from '@/components/jobs/killerQuestionsTemplates/FoldersTree';

export default {
  components: {
    GoBackButton,
    FoldersTree
  },
  data() {
    return {
      folderName: "",
      killerqFolders: null,
      selectedEditFolder: {
        title: ""
      },
      selectedSaveFolder: {
        title: ""
      },
      init: true
    }
  },
  validations: {
    folderName: { required }
  },
  computed: {
    isEditFolderSelected() {
      return !this.init && this.selectedEditFolder.title === "";
    },
    isAdmin() {
      return this.$hasRole(['admin']);
    }
  },
  mounted() {
    this.getAll();
  },
  methods: {
    getAll() {
      let url = this.isAdmin ? '/killer-questions-templates/all-admin'
          : '/killer-questions-templates/all-user';

      this.axios.get(url)
        .then( response => {
          this.killerqFolders = response.data;
        });
    },
    selectEditFolder(node) {
      this.selectedEditFolder = node;
      this.folderName = node.title;
    },
    selectSaveFolder(node) {
      if(this.selectedSaveFolder.title == node.title) {
        this.selectedSaveFolder = {
          title: ""
        };
      } else {
        this.selectedSaveFolder = node;
      }
    },
    validate() {
      this.init = false;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$toastNotification('danger', '', this.$t('pages.killer-questions-templates.manage-folder.failure', 'All fields must be filled!'));
        return false;
      } else {
        return true;
      }
    },
    updateFolder() {
      let data = {
        title: this.folderName,
        editFolder: this.selectedEditFolder.id,
        saveFolder: this.selectedSaveFolder.id || null
      }

      if(this.validate()) {
        if(this.selectedEditFolder.parent_id === null && this.selectedSaveFolder.title != "") {
          this.$toastNotification('danger', '', this.$t('pages.killer-questions-templates.manage-folder.cannot-move', 'Cannot move root folder!'));
          return;
        }
        this.axios.post('/killer-questions-templates/edit-folder', data)
          .then( () => {
            this.$router.push({ name: 'manage-killerq-templates' });
          })
          .catch();
      }
    },
    deleteFolder() {
      if(this.validate()) {
        if(this.selectedEditFolder.parent_id === null) {
          this.$toastNotification('danger', '', this.$t('pages.killer-questions-templates.manage-folder.cannot-delete', 'Cannot delete root folder!'));
          return;
        }

        const childLength = this.selectedEditFolder.killer_question_children.length;
        const h = this.$createElement
        const messageVNode = h('div', [
          h('p', { class: ['text-center'] }, [
            this.$t('pages.killer-questions-templates.manage-folder.delete-modal.selected', 'You have selected '),
            h('strong', childLength + 1),
            this.$t('pages.killer-questions-templates.manage-folder.delete-modal.folders', ' folders.'),
          ]),
          h('p', { class: ['text-center'] }, [
            this.$t('pages.killer-questions-templates.manage-folder.delete-modal.you-sure-delete', 'Are you sure you want to delete it?')
          ]),
        ])
        this.$bvModal.msgBoxConfirm([messageVNode], {
          title: this.$t('pages.killer-questions-templates.manage-folder.delete-modal.you-sure'),
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: this.$t('generic.delete'),
          cancelTitle: this.$t('generic.cancel'),
          centered: true,
          headerClass: 'delete-confirm-dialog-header'
        })
          .then(value => {
            if(value) {
              let data = {
                deleteFolder: this.selectedEditFolder.id
              }
              this.axios.post('/killer-questions-templates/delete-folder', data)
                .then( () => {
                  this.$router.push({ name: 'manage-killerq-templates' });
                })
              .catch();
            }
          });
      }
    }
  }
}
</script>

<style lang="scss">
  .delete-confirm-dialog-header {
    background: #dc3545;
  }
</style>